import React from "react";
import { motion } from "framer-motion";
import {
  Smartphone,
  Star,
  Users,
  Calendar,
  Heart,
  PenTool,
  Clock,
  Zap,
  ArrowRight,
  CheckCircle,
  FileText,
  MessageCircle,
  Phone,
  Globe,
  Layers,
  Code,
  Monitor,
  BookOpen,
  Send,
  Lightbulb,
} from "lucide-react";

const Link = ({ to, children, className }) => (
  <a href={to || "#"} className={className}>
    {children}
  </a>
);

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay }) => {
  return (
    <motion.div
      className="bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4 text-blue-400">{icon}</div>
      <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
      <p className="text-gray-400 flex-grow">{description}</p>
    </motion.div>
  );
};

// Benefits List Item Component
const BenefitsListItem = ({ text, delay }) => {
  return (
    <motion.li
      className="flex items-start"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <CheckCircle className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
      <span className="text-gray-300">{text}</span>
    </motion.li>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ quote, author, rating, delay }) => {
  return (
    <motion.div
      className="bg-slate-900 rounded-xl p-6 shadow-lg flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4 flex">
        {[...Array(rating)].map((_, i) => (
          <Star
            key={`filled-${i}`}
            className="w-5 h-5 text-yellow-400 fill-yellow-400"
          />
        ))}
        {[...Array(5 - rating)].map((_, i) => (
          <Star key={`empty-${i}`} className="w-5 h-5 text-gray-600" />
        ))}
      </div>
      <p className="text-gray-300 mb-4 italic flex-grow">"{quote}"</p>
      <p className="text-blue-400 font-medium mt-auto">{author}</p>
    </motion.div>
  );
};

// Fact Card Component
const FactCard = ({ icon, title, description, linkText, delay }) => {
  return (
    <motion.div
      className="bg-slate-900 rounded-xl p-6 shadow-lg flex flex-col h-full border border-blue-800/30 hover:border-blue-600/50 transition-colors"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="mb-4 text-blue-400">{icon}</div>
      <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
      <p className="text-gray-400 mb-4 flex-grow">{description}</p>
      <a
        href="#"
        className="text-blue-400 hover:text-blue-300 font-medium text-sm flex items-center mt-auto"
      >
        {linkText} <ArrowRight className="w-4 h-4 ml-1" />
      </a>
    </motion.div>
  );
};

// Main Landing Page Component
const App = () => {
  return (
    <div className="bg-gradient-to-b from-blue-950 via-blue-900 to-blue-950 min-h-screen text-white font-sans">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-blue-950/80 backdrop-blur-md border-b border-blue-800/30">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <motion.div
              className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-md"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2,
              }}
            >
              <PenTool className="w-4 h-4 text-white" />
            </motion.div>
            <h1 className="text-xl font-bold">
              UX<span className="text-blue-400">AI</span>School
              <span className="ml-1 text-xs bg-blue-500/20 text-blue-300 rounded px-1.5 py-0.5 font-medium">
                Madeira
              </span>
            </h1>
          </Link>
          <div className="hidden md:flex space-x-6 text-sm font-medium">
            <a
              href="#features"
              className="hover:text-blue-400 transition-colors"
            >
              Features
            </a>
            <a
              href="#courses"
              className="hover:text-blue-400 transition-colors"
            >
              Courses
            </a>
            <a
              href="#local-events"
              className="hover:text-blue-400 transition-colors"
            >
              Local Events
            </a>
            <a
              href="#benefits"
              className="hover:text-blue-400 transition-colors"
            >
              Benefits
            </a>
            <a
              href="#insights"
              className="hover:text-blue-400 transition-colors"
            >
              Resources
            </a>
            <a
              href="#testimonials"
              className="hover:text-blue-400 transition-colors"
            >
              Testimonials
            </a>
          </div>
          <Link to="/register" className="hidden md:inline-block">
            <motion.button
              className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium flex items-center hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Enroll Now <ArrowRight className="ml-1 h-4 w-4" />
            </motion.button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Hero Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block bg-blue-500/20 rounded-full px-4 py-1 mb-4 border border-blue-400/30"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <span className="text-blue-300 text-sm font-medium flex items-center">
                <Globe className="w-3 h-3 mr-1" /> Based in Funchal, Madeira
                Island
              </span>
            </motion.div>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Learn UX Design in the <br />
              <span className="text-blue-400">AI-Powered Era.</span>
            </motion.h2>
            <motion.p
              className="text-lg text-gray-300 mb-8 max-w-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              The Leading School for Aspiring Designers in Madeira – Enhance
              Your UX Skills with AI and No-Code Tools. Join our local classes
              in Funchal or learn remotely with our global community.
              <br />
              <strong className="mt-2 block text-blue-300">
                Master UX design faster and smarter than ever before.
              </strong>
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Link to="/register">
                <motion.button
                  className="w-full sm:w-auto px-8 py-3 bg-blue-600 rounded-lg font-semibold text-lg flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg hover:shadow-blue-500/30"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
              <Link to="/local-classes">
                <motion.button
                  className="w-full sm:w-auto px-8 py-3 bg-transparent border border-white/30 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Madeira Classes
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Hero Design Mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:flex justify-center items-center mt-10 lg:mt-0"
          >
            <div className="relative w-72 h-[30rem] flex items-center justify-center">
              {/* Main mockup outline */}
              <motion.div
                className="absolute w-full h-full bg-gradient-to-b from-slate-700 to-slate-800 rounded-[2.5rem] shadow-xl border-4 border-slate-600 overflow-hidden p-1.5"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {/* Design screen */}
                <div className="bg-gradient-to-b from-blue-950 to-blue-900 h-full w-full rounded-[2rem] p-4 flex flex-col overflow-hidden">
                  {/* App header */}
                  <div className="flex justify-between items-center mb-3 flex-shrink-0">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-2">
                        <PenTool className="h-3 w-3 text-white" />
                      </div>
                      <span className="text-white font-bold text-sm">
                        UXAISchool
                      </span>
                    </div>
                    {/* Example notification icon */}
                    <div className="relative">
                      <Lightbulb className="w-5 h-5 text-gray-400" />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-blue-900 text-xs flex items-center justify-center">
                        <span className="text-[8px] font-bold">3</span>
                      </div>
                    </div>
                  </div>

                  {/* Learning reminder card */}
                  <motion.div
                    className="bg-blue-800/50 rounded-lg p-3 mb-4 flex-shrink-0 shadow-md"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.5 }}
                  >
                    <h3 className="text-xs text-blue-300 mb-1 font-semibold tracking-wide">
                      UPCOMING
                    </h3>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="font-bold text-sm text-white">
                        Figma AI Workshop
                      </span>
                      <span className="text-xs bg-yellow-500/30 text-yellow-300 rounded px-2 py-0.5 font-medium">
                        Live
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-300">
                      <Calendar className="w-3 h-3 mr-1.5 flex-shrink-0" />
                      <span>Tomorrow, 6:00 PM with Sarah</span>
                    </div>
                  </motion.div>

                  {/* Course selection carousel */}
                  <motion.div
                    className="mb-4 flex-shrink-0"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.1, duration: 0.5 }}
                  >
                    <h3 className="text-xs text-blue-300 mb-2 font-semibold tracking-wide">
                      MY COURSES
                    </h3>
                    <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
                      {[
                        { name: "UX Basics", img: true, active: true },
                        { name: "AI Tools", img: true, active: false },
                        { name: "Webflow", img: true, active: false },
                        { name: "Figma Pro", img: true, active: false },
                      ].map((course, i) => (
                        <div
                          key={i}
                          className={`flex-shrink-0 text-center ${
                            course.active ? "opacity-100" : "opacity-60"
                          }`}
                        >
                          <div
                            className={`w-12 h-12 rounded-full bg-slate-600 mb-1 border-2 ${
                              course.active
                                ? "border-blue-400"
                                : "border-transparent"
                            } shadow-inner flex items-center justify-center`}
                          >
                            <PenTool className="w-6 h-6 text-slate-400" />
                          </div>
                          <span
                            className={`text-xs block ${
                              course.active
                                ? "text-blue-300 font-medium"
                                : "text-gray-400"
                            }`}
                          >
                            {course.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Quick access features */}
                  <motion.div
                    className="flex-grow flex-shrink-0 overflow-hidden"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.3, duration: 0.5 }}
                  >
                    <h3 className="text-xs text-blue-300 mb-2 font-semibold tracking-wide">
                      QUICK ACCESS
                    </h3>
                    <div className="space-y-2">
                      {[
                        { name: "Today's Lessons", icon: BookOpen },
                        { name: "AI Design Tools", icon: Layers },
                        { name: "My Projects", icon: FileText },
                      ].map((feature, i) => (
                        <div
                          key={i}
                          className="bg-slate-800/60 p-2.5 rounded-lg flex justify-between items-center hover:bg-slate-700/80 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center">
                            <feature.icon className="h-4 w-4 mr-2 text-blue-400 flex-shrink-0" />
                            <div className="text-sm font-medium text-white">
                              {feature.name}
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-gray-500" />
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Bottom navigation */}
                  <motion.div
                    className="mt-auto pt-3 flex justify-around border-t border-blue-800/30 flex-shrink-0"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                  >
                    {[
                      { label: "Learn", icon: BookOpen, active: true },
                      { label: "Projects", icon: Layers, active: false },
                      { label: "AI Tools", icon: Zap, active: false },
                      { label: "Profile", icon: Users, active: false },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`text-center ${
                          item.active
                            ? "text-blue-400"
                            : "text-gray-500 hover:text-gray-300"
                        } transition-colors`}
                      >
                        <item.icon className="h-5 w-5 mx-auto mb-0.5" />
                        <span className="text-[10px] font-medium">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          className="mt-20 md:mt-28"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="py-8 px-6 bg-slate-800/50 rounded-xl backdrop-blur-sm border border-blue-700/20 shadow-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4">
              {[
                { value: "25+", label: "Interactive Courses", delay: 0.8 },
                { value: "12", label: "Weekly Local Classes", delay: 0.9 },
                { value: "300+", label: "Madeira Graduates", delay: 1.0 },
                { value: "4.9★", label: "Student Rating", delay: 1.1 },
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: stat.delay, duration: 0.5 }}
                >
                  <h3 className="text-3xl md:text-4xl font-bold text-blue-400">
                    {stat.value}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-slate-900/70 py-20 md:py-28">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose UXAISchool?
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Our curriculum combines traditional UX design principles with
              cutting-edge AI tools and no-code platforms.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Zap className="w-10 h-10" />}
              title="AI-Enhanced Learning"
              description="Our curriculum is built around the latest AI tools like MidJourney, Figma AI, and ChatGPT, empowering you to create innovative, AI-driven designs."
              delay={0.2}
            />
            <FeatureCard
              icon={<Code className="w-10 h-10" />}
              title="No-Code, No Problem"
              description="Focus on UX design without the hassle of coding. Learn to use no-code platforms like Webflow, Bubble, and more to bring your designs to life quickly."
              delay={0.3}
            />
            <FeatureCard
              icon={<FileText className="w-10 h-10" />}
              title="Practical, Hands-On Training"
              description="Get real-world experience with design projects and AI-powered workflows that help you build a portfolio and land your first design job."
              delay={0.4}
            />
            <FeatureCard
              icon={<Globe className="w-10 h-10" />}
              title="Global Learning Community"
              description="Whether you're based in Portugal or anywhere in the world, UXAISchool offers a flexible online learning environment with access to a global community of creators."
              delay={0.5}
            />
            <FeatureCard
              icon={<MessageCircle className="w-10 h-10" />}
              title="Expert Mentorship"
              description="Connect with industry veterans who are actively working with AI and design tools. Get personalized feedback and guidance throughout your learning journey."
              delay={0.6}
            />
            <FeatureCard
              icon={<Monitor className="w-10 h-10" />}
              title="Future-Proof Skills"
              description="Stay ahead of the curve with our constantly updated curriculum that incorporates the latest AI design tools and industry best practices."
              delay={0.7}
            />
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 md:py-28 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Course Mockup */}
            <motion.div
              className="relative flex justify-center lg:justify-start"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Course Visual */}
              <div className="relative w-72 h-[30rem] bg-gradient-to-b from-slate-700 to-slate-800 rounded-[2.5rem] shadow-xl border-4 border-slate-600 p-1.5">
                {/* Screen */}
                <div className="bg-slate-900 h-full w-full rounded-[2rem] p-3 flex flex-col overflow-hidden">
                  {/* Course Header */}
                  <div className="flex items-center mb-3 pb-2 border-b border-slate-700 flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-600 rounded-full mr-2 flex-shrink-0 flex items-center justify-center">
                      <Layers className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        AI for UX Designers
                      </p>
                      <p className="text-xs text-blue-400 flex items-center">
                        <span className="w-2 h-2 bg-blue-400 rounded-full mr-1"></span>
                        Module 3 of 8
                      </p>
                    </div>
                  </div>
                  {/* Course Content */}
                  <div className="flex-grow space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 pr-1">
                    <div className="bg-blue-600/20 rounded-lg p-3">
                      <h4 className="font-medium text-blue-300 text-sm">
                        TODAY'S LESSON
                      </h4>
                      <h3 className="font-bold text-white mb-2">
                        Generative Design with AI
                      </h3>
                      <p className="text-sm text-gray-300">
                        Learn how to use AI tools to quickly generate design
                        concepts and iterate on them.
                      </p>
                      <button className="mt-3 bg-blue-600 text-white text-sm px-3 py-1 rounded flex items-center">
                        Continue Learning{" "}
                        <ArrowRight className="ml-1 w-3 h-3" />
                      </button>
                    </div>

                    <div className="bg-slate-800 rounded-lg p-3">
                      <h4 className="font-medium text-gray-400 text-sm mb-2">
                        COURSE OUTLINE
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-sm text-gray-300">
                              Introduction to AI Design
                            </span>
                          </div>
                          <span className="text-xs text-green-400">
                            Completed
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <span className="text-sm text-gray-300">
                              Midjourney for UX
                            </span>
                          </div>
                          <span className="text-xs text-green-400">
                            Completed
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 border-2 border-blue-400 rounded-full mr-2 flex items-center justify-center">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            </div>
                            <span className="text-sm text-white font-medium">
                              Generative Design with AI
                            </span>
                          </div>
                          <span className="text-xs text-blue-400">
                            In Progress
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 border border-gray-500 rounded-full mr-2"></div>
                            <span className="text-sm text-gray-400">
                              ChatGPT for Design
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            Upcoming
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Course Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Our Courses: Build Your UX Design Skillset with AI Tools
              </h2>
              <p className="text-lg text-gray-300 mb-6">
                At UXAISchool, we offer a comprehensive range of courses
                designed for beginners and aspiring designers who want to learn
                UX design while incorporating AI and no-code tools.
              </p>
              <ul className="space-y-3 text-lg mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>
                    <span className="text-blue-300 font-medium">
                      Intro to UX Design:
                    </span>{" "}
                    Learn the fundamentals of UX/UI, from user research to
                    wireframing and prototyping.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>
                    <span className="text-blue-300 font-medium">
                      AI-Powered UX Tools:
                    </span>{" "}
                    Master AI tools for design, including using AI to automate
                    tasks, generate ideas, and enhance creativity.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>
                    <span className="text-blue-300 font-medium">
                      No-Code Prototyping & Web Design:
                    </span>{" "}
                    Build web prototypes using no-code tools like Webflow and
                    Figma with AI integrations.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>
                    <span className="text-blue-300 font-medium">
                      Design Thinking & AI Integration:
                    </span>{" "}
                    Dive deep into the design thinking process and how AI can
                    optimize each step.
                  </span>
                </li>
              </ul>
              <Link to="/courses">
                <motion.button
                  className="px-6 py-3 bg-blue-600 rounded-lg font-medium text-lg flex items-center hover:bg-blue-700 transition-colors shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View All Courses <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 md:py-28 bg-slate-900/70">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Learn Design for the AI Era
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              UXAISchool empowers both beginners and experienced designers with
              modern tools for creating exceptional user experiences.
            </p>
          </motion.div>

          {/* Benefits for Beginners */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-semibold mb-6 text-blue-400">
                For Beginners: Launch Your Design Career
              </h3>
              <ul className="space-y-4 text-lg">
                <BenefitsListItem
                  text="Start from zero with a structured curriculum that builds your skills progressively."
                  delay={0.2}
                />
                <BenefitsListItem
                  text="Learn design fundamentals while simultaneously mastering the latest AI tools."
                  delay={0.3}
                />
                <BenefitsListItem
                  text="Build an impressive portfolio with AI-enhanced projects that stand out to employers."
                  delay={0.4}
                />
                <BenefitsListItem
                  text="Get mentorship from experienced designers who understand the current job market."
                  delay={0.5}
                />
                <BenefitsListItem
                  text="Join a supportive community of fellow learners to collaborate and grow together."
                  delay={0.6}
                />
              </ul>
            </motion.div>

            {/* Visual for Beginners */}
            <motion.div
              className="relative rounded-xl overflow-hidden shadow-2xl bg-slate-800 border border-blue-700/20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-blue-900/50 to-blue-800/30 p-6 flex flex-col items-center justify-center">
                <div className="text-center">
                  <PenTool className="w-16 h-16 text-blue-500 mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2 text-white">
                    Start Your Design Journey
                  </h4>
                  <p className="text-gray-400 max-w-xs">
                    From fundamentals to advanced AI tools, we'll guide you
                    every step of the way.
                  </p>
                </div>
                <div className="mt-6 w-full max-w-sm bg-slate-900 p-4 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-300">
                      Your Learning Path
                    </span>
                    <span className="text-xs text-gray-500">Progress: 35%</span>
                  </div>
                  <div className="h-3 w-full bg-slate-700/50 rounded-full mb-4">
                    <div className="h-full w-1/3 bg-blue-600 rounded-full"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-300">
                        UX Design Fundamentals
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <div className="w-4 h-4 border-2 border-blue-400 rounded-full mr-2 flex items-center justify-center">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      </div>
                      <span className="text-white">
                        Intro to AI Design Tools
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Benefits for Experienced Designers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Visual for Experienced Designers */}
            <motion.div
              className="order-2 lg:order-1 relative rounded-xl overflow-hidden shadow-2xl bg-slate-800 border border-blue-700/20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-slate-900 to-slate-800 p-6 flex flex-col items-center justify-center">
                <div className="text-center">
                  <Zap className="w-16 h-16 text-blue-500 mx-auto mb-4 opacity-80" />
                  <h4 className="text-xl font-semibold mb-2 text-white">
                    Supercharge Your Skills
                  </h4>
                  <p className="text-gray-400 max-w-xs">
                    Level up your existing design knowledge with the most
                    powerful AI design tools.
                  </p>
                </div>
                <div className="mt-6 w-full max-w-sm bg-slate-900 p-4 rounded-lg shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-blue-300">
                      AI Tool Proficiency
                    </span>
                    <span className="text-xs bg-blue-500/30 text-blue-300 rounded px-2 py-0.5 font-medium">
                      Advanced
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span>Midjourney</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-3 h-3 rounded-full ml-1 ${
                              i < 4 ? "bg-blue-500" : "bg-slate-700"
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Figma AI</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-3 h-3 rounded-full ml-1 ${
                              i < 5 ? "bg-blue-500" : "bg-slate-700"
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Text for Experienced Designers */}
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-semibold mb-6 text-blue-400">
                For Professionals: Upgrade Your Skillset
              </h3>
              <ul className="space-y-4 text-lg">
                <BenefitsListItem
                  text="Stay ahead of the curve with cutting-edge AI design tools and techniques."
                  delay={0.6}
                />
                <BenefitsListItem
                  text="Learn how to integrate AI into your existing design workflow for 10x productivity."
                  delay={0.7}
                />
                <BenefitsListItem
                  text="Develop expertise in no-code platforms to rapidly prototype and deliver projects."
                  delay={0.8}
                />
                <BenefitsListItem
                  text="Connect with a network of forward-thinking designers embracing AI technologies."
                  delay={0.9}
                />
                <BenefitsListItem
                  text="Future-proof your career in an industry being transformed by artificial intelligence."
                  delay={1.0}
                />
              </ul>
              <Link to="/advanced-courses" className="mt-6 inline-block">
                <motion.button
                  className="px-6 py-2 bg-blue-700 rounded-lg font-medium text-base flex items-center hover:bg-blue-800 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore Advanced Courses{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO-Enhanced Madeira UX Events & Training Section */}
      <section
        id="madeira-ux-events"
        className="py-20 md:py-28 bg-gradient-to-b from-slate-900/90 via-blue-950/40 to-slate-900/90 relative"
        aria-labelledby="madeira-events-heading"
      >
        {/* SEO-optimized structured data (hidden from view but valuable for search engines) */}
        <div className="hidden" itemScope itemType="https://schema.org/Event">
          <meta itemProp="name" content="UX Design Classes in Madeira" />
          <meta
            itemProp="description"
            content="Join our UX design classes and events in Funchal, Madeira. Learn UI/UX design with AI tools through in-person workshops and networking events."
          />
          <meta itemProp="startDate" content="2025-05-18T19:00" />
          <meta itemProp="endDate" content="2025-05-18T21:00" />
          <div
            itemProp="location"
            itemScope
            itemType="https://schema.org/Place"
          >
            <meta itemProp="name" content="Cowork Funchal" />
            <div
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
            >
              <meta itemProp="streetAddress" content="Rua dos Ferreiros" />
              <meta itemProp="addressLocality" content="Funchal" />
              <meta itemProp="addressRegion" content="Madeira" />
              <meta itemProp="addressCountry" content="Portugal" />
            </div>
          </div>
          <meta
            itemProp="image"
            content="https://uxaischool.com/images/madeira-ux-events.jpg"
          />
          <div
            itemProp="organizer"
            itemScope
            itemType="https://schema.org/Organization"
          >
            <meta itemProp="name" content="UXAISchool Madeira" />
            <meta itemProp="url" content="https://uxaischool.com" />
          </div>
        </div>

        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-0 w-64 h-64 bg-blue-600/20 rounded-full filter blur-[80px]"></div>
          <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-indigo-700/10 rounded-full filter blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Keyword-rich badge for better SEO */}
            <span className="inline-block bg-blue-500/20 text-blue-300 text-sm font-medium rounded-full px-4 py-1 mb-4 border border-blue-400/30">
              UX Design Events in Funchal, Madeira
            </span>

            {/* H2 with target keywords for SEO */}
            <h2
              id="madeira-events-heading"
              className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200"
            >
              Madeira UX/UI Design Events & In-Person Training
            </h2>

            {/* Rich, keyword-optimized description */}
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Join Madeira's premier UX design community in Funchal. Our
              in-person classes, workshops, and networking events connect local
              designers with global expertise in user experience design, AI
              tools, and no-code development.
            </p>
          </motion.div>

          {/* Image banner - adding visual element improves engagement and SEO (images can rank in image search) */}
          <motion.div
            className="relative h-48 md:h-64 rounded-xl overflow-hidden mb-16 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-transparent z-10"></div>
            <img
              src="/api/placeholder/1200/400"
              alt="UX Design Workshop in Funchal, Madeira"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 z-20 flex items-center justify-start p-8 md:p-12">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Learn UX Design in Paradise
                </h3>
                <p className="text-gray-200 max-w-md">
                  Combine your passion for design with Madeira's inspiring
                  environment. In-person workshops in the heart of Funchal.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Class Schedule Card - Enhanced with more keyword-rich content */}
            <motion.div
              className="bg-slate-800/80 rounded-xl p-6 shadow-lg border border-blue-800/30 hover:border-blue-600/50 transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex items-center mb-4">
                <Calendar className="w-10 h-10 text-blue-400 mr-3" />
                <div>
                  <span className="text-xs uppercase tracking-wider text-blue-300">
                    Hands-on Learning
                  </span>
                  <h3 className="text-xl font-bold text-white">
                    UX Design Classes in Funchal
                  </h3>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Small group sessions (max 8 students) at our centrally located
                Funchal studio. Receive personalized instruction and real-time
                feedback from industry professionals.
              </p>
              <div className="space-y-3 mt-4">
                <div className="flex justify-between items-center p-2 rounded bg-slate-700/40 hover:bg-slate-700/60 transition-colors">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-white font-medium">
                      Beginner UX/UI Design
                    </span>
                  </div>
                  <span className="text-blue-300 text-sm">
                    Mon & Wed, 18:00-20:00
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 rounded bg-slate-700/40 hover:bg-slate-700/60 transition-colors">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
                    <span className="text-white font-medium">
                      AI Tools for Designers
                    </span>
                  </div>
                  <span className="text-blue-300 text-sm">
                    Tuesdays, 19:00-21:00
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 rounded bg-slate-700/40 hover:bg-slate-700/60 transition-colors">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                    <span className="text-white font-medium">
                      UX Portfolio Building
                    </span>
                  </div>
                  <span className="text-blue-300 text-sm">
                    Thursdays, 18:00-20:00
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 rounded bg-slate-700/40 hover:bg-slate-700/60 transition-colors">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                    <span className="text-white font-medium">
                      Figma Masterclass
                    </span>
                  </div>
                  <span className="text-blue-300 text-sm">
                    Fridays, 17:00-19:00
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-blue-900/30 flex justify-between items-center">
                <div>
                  <span className="block text-gray-400 text-sm">
                    Starting at
                  </span>
                  <span className="text-white font-bold">€85/class</span>
                </div>
                <Link
                  to="/madeira-ux-classes"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm flex items-center transition-colors"
                >
                  See Class Schedule <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>

            {/* UX Meetups Card - Enhanced with more SEO content */}
            <motion.div
              className="bg-slate-800/80 rounded-xl p-6 shadow-lg border border-blue-800/30 hover:border-blue-600/50 transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="flex items-center mb-4">
                <Users className="w-10 h-10 text-blue-400 mr-3" />
                <div>
                  <span className="text-xs uppercase tracking-wider text-blue-300">
                    Community Events
                  </span>
                  <h3 className="text-xl font-bold text-white">
                    Madeira UX Meetups
                  </h3>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Network with Madeira's growing design community every month.
                Share knowledge, discuss industry trends, and connect with
                potential clients and employers.
              </p>
              <div className="p-4 bg-gradient-to-br from-blue-900/40 to-indigo-900/20 rounded-lg mt-4 border border-blue-700/30">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-600/30 rounded mr-3">
                    <Calendar className="w-5 h-5 text-blue-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white">
                      Next Meetup: Design Systems & AI
                    </h4>
                    <p className="text-sm text-blue-300">
                      Featured Speaker: Maria Santos, Lead Designer at
                      TechMadeira
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <div className="text-sm text-gray-300 flex items-center">
                    <Calendar className="w-3 h-3 mr-1.5 text-gray-400" />
                    <span>May 18th, 2025 • 19:00-21:00</span>
                  </div>
                  <div className="text-sm text-gray-300 flex items-center">
                    <Globe className="w-3 h-3 mr-1.5 text-gray-400" />
                    <span>Cowork Funchal, Rua dos Ferreiros</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-blue-800/30 flex justify-between items-center">
                  <span className="text-white">
                    Free admission • 45 attending
                  </span>
                  <span className="px-2 py-1 bg-blue-600/30 text-blue-300 text-xs rounded">
                    RSVP Open
                  </span>
                </div>
              </div>
              <Link
                to="/madeira-ux-meetups"
                className="mt-6 inline-block text-blue-400 hover:text-blue-300 font-medium text-sm flex items-center"
              >
                See All Upcoming Meetups <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>

            {/* UX Hackathon Card - Enhanced with more conversion-focused content */}
            <motion.div
              className="bg-slate-800/80 rounded-xl p-6 shadow-lg border border-blue-800/30 hover:border-blue-600/50 transition-all"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex items-center mb-4">
                <Zap className="w-10 h-10 text-blue-400 mr-3" />
                <div>
                  <span className="text-xs uppercase tracking-wider text-blue-300">
                    Immersive Experience
                  </span>
                  <h3 className="text-xl font-bold text-white">
                    Madeira UX Hackathons
                  </h3>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                Intensive weekend events that challenge participants to solve
                real design problems for Madeira's tourism, tech, and
                sustainability sectors. Build your portfolio while making an
                impact.
              </p>
              <div className="p-4 bg-gradient-to-br from-yellow-900/30 to-orange-900/20 rounded-lg mt-4 border border-yellow-700/30">
                <div className="px-3 py-1 bg-yellow-600/20 text-yellow-300 text-xs font-medium rounded-full inline-block mb-2">
                  UPCOMING EVENT
                </div>
                <h4 className="text-lg font-medium text-white mb-1">
                  Summer UX/UI Hackathon 2025
                </h4>
                <p className="text-sm text-gray-300 mb-3">
                  Design digital solutions for Madeira's sustainable tourism
                  initiatives. Work with local stakeholders to create impactful
                  user experiences.
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1.5 text-yellow-400" />
                    <span className="text-gray-300">June 24-26, 2025</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-3 h-3 mr-1.5 text-yellow-400" />
                    <span className="text-gray-300">Startup Madeira</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-3 h-3 mr-1.5 text-yellow-400" />
                    <span className="text-gray-300">Teams of 2-4</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="w-3 h-3 mr-1.5 text-yellow-400" />
                    <span className="text-gray-300">€2,000 in prizes</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-blue-900/30 flex justify-between items-center">
                <span className="text-white font-medium">
                  Limited to 30 participants
                </span>
                <Link
                  to="/madeira-ux-hackathons"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white rounded-lg font-medium text-sm flex items-center transition-all"
                >
                  Register Now <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Newsletter signup - great for conversions and SEO engagement signals 
    <motion.div
      className="bg-gradient-to-r from-blue-900/40 to-indigo-900/30 rounded-xl p-6 md:p-8 border border-blue-700/30 mb-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.6 }}
    >
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="text-xl font-bold text-white mb-2">Stay Updated on Madeira UX Events</h3>
          <p className="text-gray-300">
            Get notified about upcoming classes, meetups, and exclusive design resources for the Madeira community.
          </p>
        </div>
        <div className="w-full md:w-2/5">
          <div className="flex">
            <input 
              type="email" 
              placeholder="Your email address" 
              className="flex-grow bg-slate-800/80 border border-blue-800/50 rounded-l-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-lg font-medium transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            We respect your privacy. Unsubscribe anytime. View our <a href="/privacy" className="text-blue-400 hover:underline">privacy policy</a>.
          </p>
        </div>
      </div>
    </motion.div>

    {/* FAQ Section - great for SEO as it answers common user questions 
    <motion.div
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.7 }}
    >
      <h3 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h3>
      <div className="space-y-4">
        <div className="bg-slate-800/60 rounded-lg p-4 border border-blue-900/30">
          <h4 className="font-medium text-white mb-2">Where are UXAISchool classes held in Madeira?</h4>
          <p className="text-gray-300">
            All our in-person classes and most events are held at our studio in central Funchal, 
            conveniently located near public transportation and with parking nearby. Some special 
            events like hackathons may be held at partner locations like Startup Madeira.
          </p>
        </div>
        <div className="bg-slate-800/60 rounded-lg p-4 border border-blue-900/30">
          <h4 className="font-medium text-white mb-2">Do I need design experience to join UXAISchool events in Madeira?</h4>
          <p className="text-gray-300">
            Not at all! We welcome participants of all skill levels. Our beginner classes are specifically designed 
            for those with no prior design experience, while our advanced workshops and hackathons offer challenges 
            for seasoned designers.
          </p>
        </div>
        <div className="bg-slate-800/60 rounded-lg p-4 border border-blue-900/30">
          <h4 className="font-medium text-white mb-2">Are UXAISchool events in English or Portuguese?</h4>
          <p className="text-gray-300">
            We offer events in both languages. Our core classes are available in both English and Portuguese, 
            and we always specify the language for each event. Many of our instructors are bilingual, and our 
            community accommodates both local residents and international participants.
          </p>
        </div>
      </div>
    </motion.div>
*/}
        </div>
      </section>

      {/* Resources Section */}
      <section id="insights" className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              UX & AI Design Resources
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Stay informed with exclusive articles, guides, and tips from our
              expert instructors. Knowledge is the foundation of great design.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FactCard
              icon={<BookOpen className="w-10 h-10" />}
              title="The Future of UX in an AI World"
              description="How is AI transforming UX design? Discover the emerging trends, tools, and skills that will define the next generation of user experience design."
              linkText="Read Full Article"
              delay={0.2}
            />
            <FactCard
              icon={<Lightbulb className="w-10 h-10" />}
              title="10 AI Prompts Every UX Designer Should Know"
              description="Learn how to craft effective prompts for AI tools like Midjourney and ChatGPT to enhance your design process and generate better results faster."
              linkText="Get the Prompts"
              delay={0.4}
            />
            <FactCard
              icon={<Layers className="w-10 h-10" />}
              title="Madeira's Design Scene"
              description="Explore the growing UX design community in Madeira and how local businesses are embracing user-centered design approaches to reach global markets."
              linkText="Read Local Insights"
              delay={0.6}
            />
          </div>
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to="/resources">
              <button className="px-6 py-2 bg-transparent border border-blue-600 text-blue-400 rounded-lg font-medium hover:bg-blue-600/20 transition-colors">
                Explore All Resources
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section with Improved Design */}
      <section
        id="testimonials"
        className="bg-gradient-to-br from-slate-900 via-blue-900/30 to-slate-900 py-20 md:py-28 relative overflow-hidden"
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-600 rounded-full filter blur-[120px]"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-blue-500/20 rounded-full px-4 py-1 mb-4 border border-blue-400/30 text-blue-300 text-sm font-medium">
              Success Stories
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Transforming Careers in Madeira & Beyond
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Hear from our alumni who have leveraged UX design skills and AI
              tools to elevate their careers both locally and internationally.
            </p>
          </motion.div>

          {/* Featured Testimonial */}
          <motion.div
            className="bg-slate-800/60 rounded-xl p-6 md:p-8 shadow-xl backdrop-blur-sm border border-blue-500/20 mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              <div className="md:w-1/3 flex flex-col items-center text-center md:text-left md:items-start">
                <div className="w-24 h-24 rounded-full border-4 border-blue-400 mb-4 overflow-hidden">
                  <img
                    src="/api/placeholder/240/240"
                    alt="João Silva"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-bold text-white mb-1">
                  João Silva
                </h4>
                <p className="text-blue-400 mb-2">
                  Funchal Digital Agency Founder
                </p>
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={`featured-star-${i}`}
                      className="w-5 h-5 text-yellow-400 fill-yellow-400"
                    />
                  ))}
                </div>
              </div>
              <div className="md:w-2/3">
                <div className="text-4xl text-blue-500 font-serif mb-4">"</div>
                <p className="text-lg text-gray-300 italic mb-4">
                  After 10 years in traditional graphic design here in Madeira,
                  UXAISchool completely changed my career trajectory. Learning
                  to combine UX principles with AI tools allowed me to launch my
                  own digital agency serving both local tourism businesses and
                  international clients. The in-person workshops in Funchal were
                  invaluable for building my local network while the online
                  curriculum gave me global perspectives.
                </p>
                <p className="text-white font-medium">
                  Our agency now employs 5 designers, 3 of whom are also
                  UXAISchool graduates!
                </p>
              </div>
            </div>
          </motion.div>

          {/* Regular Testimonial Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="bg-slate-800/40 rounded-xl p-6 shadow-lg border border-blue-600/10 hover:border-blue-500/30 transition-all flex flex-col h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="mb-4 flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={`star1-${i}`}
                    className="w-5 h-5 text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic flex-grow">
                "The AI tools I learned at UXAISchool helped me speed up my
                design process by 3x. Working remotely from Madeira for clients
                across Europe is now my reality, and I'm charging rates I never
                thought possible."
              </p>
              <div className="flex items-center mt-auto pt-4 border-t border-blue-900/30">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold mr-3">
                  MR
                </div>
                <div>
                  <p className="text-white font-medium">Miguel Rodrigues</p>
                  <p className="text-blue-400 text-sm">Freelance UX Designer</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-slate-800/40 rounded-xl p-6 shadow-lg border border-blue-600/10 hover:border-blue-500/30 transition-all flex flex-col h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="mb-4 flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={`star2-${i}`}
                    className="w-5 h-5 text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic flex-grow">
                "As a former teacher with no design background, I was
                intimidated at first. The structured curriculum and supportive
                Madeira community made it easy to pick up UX concepts and create
                designs that now help local businesses compete globally."
              </p>
              <div className="flex items-center mt-auto pt-4 border-t border-blue-900/30">
                <div className="w-10 h-10 rounded-full bg-pink-600 flex items-center justify-center text-white font-bold mr-3">
                  SC
                </div>
                <div>
                  <p className="text-white font-medium">Sofia Costa</p>
                  <p className="text-blue-400 text-sm">Career Changer</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-slate-800/40 rounded-xl p-6 shadow-lg border border-blue-600/10 hover:border-blue-500/30 transition-all flex flex-col h-full"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="mb-4 flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={`star3-${i}`}
                    className="w-5 h-5 text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
              <p className="text-gray-300 mb-6 italic flex-grow">
                "The combination of in-person workshops in Funchal and online
                courses gave me skills that were immediately applicable. I now
                lead UX at a startup that's building solutions for the tourism
                industry in Madeira and across Portugal."
              </p>
              <div className="flex items-center mt-auto pt-4 border-t border-blue-900/30">
                <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold mr-3">
                  DT
                </div>
                <div>
                  <p className="text-white font-medium">David Teixeira</p>
                  <p className="text-blue-400 text-sm">UX Team Lead</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Call to Action */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="text-gray-300 mb-6">
              Join our community of 300+ successful graduates from Madeira and
              around the world.
            </p>
            <Link to="/success-stories">
              <button className="px-6 py-2 bg-transparent border border-blue-600 text-blue-400 rounded-lg font-medium hover:bg-blue-600/20 transition-colors">
                Read More Success Stories
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Call to Action (CTA) Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto bg-gradient-to-r from-blue-800 to-blue-700 rounded-2xl p-8 md:p-16 shadow-2xl text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-6 text-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Ready to Master UX Design with AI?
            </motion.h2>
            <motion.p
              className="text-lg text-blue-100 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Join UXAISchool today and gain the competitive edge you need in
              today's design landscape. Start designing smarter, faster, and
              more effectively with AI-powered tools.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/register">
                <motion.button
                  className="px-10 py-4 bg-white text-blue-800 rounded-lg font-semibold text-xl flex items-center justify-center mx-auto hover:bg-blue-50 transition-colors shadow-lg hover:shadow-blue-200/30"
                  whileHover={{
                    scale: 1.05,
                    transition: { type: "spring", stiffness: 300 },
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Enroll Now <Zap className="ml-2 h-5 w-5 text-yellow-500" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-blue-800/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Logo & Brand */}
            <div className="flex items-center mb-6 md:mb-0">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center mr-2">
                <PenTool className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                UX<span className="text-blue-400">AI</span>School
              </h3>
            </div>

            {/* Footer Navigation */}
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <ul className="flex flex-wrap justify-center md:justify-start space-x-6 text-sm">
                <li>
                  <a
                    href="#features"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#courses"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Courses
                  </a>
                </li>
                <li>
                  <a
                    href="#benefits"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Benefits
                  </a>
                </li>
                <li>
                  <a
                    href="#insights"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Resources
                  </a>
                </li>
                <li>
                  <a
                    href="#testimonials"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Testimonials
                  </a>
                </li>
                <li>
                  <Link
                    to="/instructors"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    For Instructors
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-gray-400 hover:text-blue-400 transition-colors"
                  >
                    Terms
                  </Link>
                </li>
              </ul>
            </div>

            {/* Social Media Icons */}
            <div className="flex space-x-4">
              <a
                href="#"
                aria-label="Facebook"
                className="text-gray-500 hover:text-blue-400 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="text-gray-500 hover:text-blue-400 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </a>
              <a
                href="#"
                aria-label="Twitter"
                className="text-gray-500 hover:text-blue-400 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                </svg>
              </a>
            </div>
          </div>

          {/* Copyright & Slogan */}
          <div className="mt-8 pt-8 border-t border-blue-800/30 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} UXAISchool. All rights reserved.</p>
            <p className="mt-1 italic">
              UXAISchool – Learn UX Design in the AI-Powered Era
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
